/**
 * jobs.js
 * Path: frontend/src/api/jobs.js
 * Description: Wywołania API zleceń, załączników i komunikatora.
 * Related: pages/CalendarPage.jsx, pages/JobDetailPage.jsx
 */

import { api, authHeaders } from "./apiBase";

function normalizeAttachment(item = {}, jobId) {
  return {
    id: item.id,
    jobId: item.job_id || item.jobId || jobId,
    messageId: item.message_id || item.messageId || null,
    originalName: item.original_name || item.originalName || "",
    mimeType: item.mime_type || item.mimeType || "",
    sizeBytes: Number(item.size_bytes || item.sizeBytes || 0),
    uploadedBy: item.uploaded_by || item.uploadedBy || null,
    createdAt: item.created_at || item.createdAt || null,
  };
}

function normalizeMessage(item = {}) {
  return {
    id: item.id,
    body: item.body || "",
    authorId: item.author_id || item.authorId || null,
    authorName: item.author_name || item.authorName || item.author_email || item.authorEmail || "Użytkownik",
    mentions: Array.isArray(item.mentions) ? item.mentions : [],
    meta: item.meta && typeof item.meta === "object" ? item.meta : {},
    isEdited: Boolean(item.is_edited || item.isEdited),
    editedAt: item.edited_at || item.editedAt || null,
    createdAt: item.created_at || item.createdAt || null,
    attachments: Array.isArray(item.attachments)
      ? item.attachments.map((entry) => normalizeAttachment(entry, item.job_id || item.jobId || null))
      : [],
  };
}

function normalizeJob(item = {}) {
  return {
    id: item.id,
    title: item.title || "",
    customerName: item.customer_name || item.customerName || "",
    customerPhone: item.customer_phone || item.customerPhone || "",
    address: item.address || "",
    scope: item.scope || "",
    color: item.color || "",
    startAt: item.start_at || item.startAt || "",
    endAt: item.end_at || item.endAt || "",
    assignedUserIds: item.assigned_user_ids || item.assignedUserIds || [],
    createdAt: item.created_at || item.createdAt || null,
    updatedAt: item.updated_at || item.updatedAt || null,
    attachments: Array.isArray(item.attachments) ? item.attachments.map((entry) => normalizeAttachment(entry, item.id)) : [],
    messages: Array.isArray(item.messages) ? item.messages.map(normalizeMessage) : [],
  };
}

export async function listJobs(token, { from, to }) {
  const response = await api.get("/jobs", {
    headers: authHeaders(token),
    params: { from, to },
  });
  return Array.isArray(response.data) ? response.data.map(normalizeJob) : [];
}

export async function getJob(token, id) {
  const response = await api.get(`/jobs/${id}`, { headers: authHeaders(token) });
  return normalizeJob(response.data);
}

export async function createJob(token, payload) {
  const response = await api.post("/jobs", payload, { headers: authHeaders(token) });
  return normalizeJob(response.data);
}

export async function updateJob(token, id, payload) {
  const response = await api.patch(`/jobs/${id}`, payload, { headers: authHeaders(token) });
  return normalizeJob(response.data);
}

export async function deleteJob(token, id) {
  const response = await api.delete(`/jobs/${id}`, { headers: authHeaders(token) });
  return response.data;
}

export async function sendJobMessage(token, id, payload = {}) {
  const hasFiles = Array.isArray(payload.files) && payload.files.length > 0;
  if (hasFiles) {
    const formData = new FormData();
    formData.append("body", payload.body || "");
    formData.append("mentions", JSON.stringify(Array.isArray(payload.mentions) ? payload.mentions : []));
    if (payload.replyToMessageId) {
      formData.append("replyToMessageId", String(payload.replyToMessageId));
    }
    payload.files.forEach((file) => {
      formData.append("files", file);
    });
    const response = await api.post(`/jobs/${id}/messages`, formData, { headers: authHeaders(token) });
    return normalizeMessage(response.data);
  }

  const response = await api.post(
    `/jobs/${id}/messages`,
    {
      body: payload.body || "",
      mentions: Array.isArray(payload.mentions) ? payload.mentions : [],
      replyToMessageId: payload.replyToMessageId || null,
    },
    { headers: authHeaders(token) }
  );
  return normalizeMessage(response.data);
}

export async function editJobMessage(token, id, messageId, payload = {}) {
  const response = await api.patch(
    `/jobs/${id}/messages/${messageId}`,
    {
      body: payload.body || "",
      mentions: Array.isArray(payload.mentions) ? payload.mentions : [],
    },
    { headers: authHeaders(token) }
  );
  return normalizeMessage(response.data);
}

export async function uploadJobAttachment(token, id, file) {
  const formData = new FormData();
  formData.append("file", file);
  const response = await api.post(`/jobs/${id}/attachments`, formData, {
    headers: authHeaders(token),
  });
  return normalizeAttachment(response.data, id);
}

export async function deleteJobAttachment(token, id, attachmentId) {
  await api.delete(`/jobs/${id}/attachments/${attachmentId}`, {
    headers: authHeaders(token),
  });
}

export async function downloadJobAttachment(token, id, attachmentId, originalName) {
  const response = await api.get(`/jobs/${id}/attachments/${attachmentId}`, {
    headers: authHeaders(token),
    responseType: "blob",
  });

  const url = window.URL.createObjectURL(response.data);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = originalName || "attachment";
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.URL.revokeObjectURL(url);
}

export async function fetchJobAttachmentBlobUrl(token, id, attachmentId) {
  const response = await api.get(`/jobs/${id}/attachments/${attachmentId}`, {
    headers: authHeaders(token),
    responseType: "blob",
  });

  return window.URL.createObjectURL(response.data);
}
